import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { FaArrowRight, FaDownload, FaCode, FaMicrochip, FaBatteryFull } from 'react-icons/fa'
import profilePhoto from '/photo/fouad-aladhami-embedded-software-engineer.png'
import ParticleBackground from '../../components/common/ParticleBackground/ParticleBackground'
import './Home.css'

const Home = () => {
  const skills = [
    { icon: <FaMicrochip />, title: 'Embedded Systems', description: 'STM32 microcontroller development and embedded C programming' },
    { icon: <FaBatteryFull />, title: 'BMS Development', description: 'Battery Management Systems for electric vehicles and industrial applications' },
    { icon: <FaCode />, title: 'Software Engineering', description: 'Full-stack development with Python, MATLAB, and modern frameworks' }
  ]

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Fouad Aladhami",
    "jobTitle": "Embedded Software Engineer",
    "description": "Embedded Software Engineer specializing in Battery Management Systems and STM32 microcontroller development",
    "image": `${window.location.origin}/photo/fouad-aladhami-embedded-software-engineer.png`,
    "url": window.location.origin,
    "sameAs": [],
    "knowsAbout": ["Embedded Systems", "Battery Management Systems", "STM32", "Embedded C", "Python", "MATLAB"],
    "alumniOf": "University",
    "workLocation": {
      "@type": "Place",
      "name": "Remote/On-site"
    }
  }

  return (
    <div className="home">
      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      {/* Hero Section */}
      <section className="hero" itemScope itemType="https://schema.org/Person">
        <ParticleBackground />
        <div className="container">
          <div className="hero-content">
            <motion.div 
              className="hero-text"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>
                Hi, I'm <span className="text-primary" itemProp="name">Fouad Aladhami</span>
              </h1>
              <h2 className="hero-subtitle" itemProp="jobTitle">Embedded Software Engineer</h2>
              <p className="hero-description" itemProp="description">
                Specialized in Battery Management Systems (BMS) and STM32 microcontroller development. 
                Passionate about creating efficient, reliable embedded solutions for electric vehicles 
                and IoT applications.
              </p>
              <div className="hero-buttons">
                <Link to="/portfolio" className="btn btn-primary">
                  View My Work <FaArrowRight />
                </Link>
                <Link to="/contact" className="btn btn-secondary">
                  Get In Touch
                </Link>
              </div>
            </motion.div>

            <motion.div 
              className="hero-image"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img 
                src={profilePhoto} 
                alt="Fouad Aladhami"
                title="Fouad Aladhami - Professional Photo"
                className="profile-photo"
                loading="eager"
                itemProp="image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Skills Preview Section */}
      <section className="skills-preview section-padding bg-secondary">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>What I Do</h2>
            <p>Specialized expertise in embedded systems and modern software development</p>
          </motion.div>

          <div className="skills-grid">
            {skills.map((skill, index) => (
              <motion.div 
                key={index}
                className="skill-card card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="skill-icon">
                  {skill.icon}
                </div>
                <h3>{skill.title}</h3>
                <p>{skill.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Stats Section */}
      <section className="stats section-padding">
        <div className="container">
          <div className="stats-grid">
            <motion.div 
              className="stat-item"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3>3+</h3>
              <p>Years Experience</p>
            </motion.div>
            <motion.div 
              className="stat-item"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>10+</h3>
              <p>Projects Completed</p>
            </motion.div>
            <motion.div 
              className="stat-item"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>1</h3>
              <p>Research Publications</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta section-padding bg-primary">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Work Together?</h2>
            <p>Let's discuss your next embedded systems project or collaboration opportunity.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-accent">
                Start a Conversation
              </Link>
              <a href="https://drive.google.com/uc?export=download&id=1u8vxtGp75aIlV2fW3vxWkTHrEY5ixL9z" className="btn btn-secondary" target="_blank" rel="noopener noreferrer">
                <FaDownload /> Download Resume
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home
